import { useState, useMemo } from 'react';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTimeline } from '@/hooks/use-staffing-board';
import type { TimelineAssignment } from '@/hooks/use-staffing-board';
import { useOrgToday } from '@/hooks/use-settings';
import { formatDateOnly } from '@/lib/date-utils';

type RangePreset = '1week' | '2weeks' | 'month';

const PROJECT_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.getFullYear(), d.getMonth(), diff);
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(d: Date, n: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + n);
  return result;
}

function daysBetween(a: Date, b: Date): number {
  const msPerDay = 86400000;
  return Math.round((b.getTime() - a.getTime()) / msPerDay);
}

function getDaysInRange(start: Date, end: Date): Date[] {
  const days: Date[] = [];
  let current = new Date(start);
  while (current <= end) {
    days.push(new Date(current));
    current = addDays(current, 1);
  }
  return days;
}

function formatShortDate(d: Date): string {
  return formatDateOnly(formatDate(d), '—', { month: 'short', day: 'numeric' });
}

function formatDayLabel(d: Date): string {
  return formatDateOnly(formatDate(d), '—', { weekday: 'short' });
}

export function TimelineView() {
  const orgToday = useOrgToday();
  const today = useMemo(() => parseLocalDate(orgToday), [orgToday]);
  const [rangePreset, setRangePreset] = useState<RangePreset>('1week');
  const [rangeStart, setRangeStart] = useState<Date>(() => getMonday(today));

  const rangeEnd = useMemo(() => {
    switch (rangePreset) {
      case '1week':
        return addDays(rangeStart, 6);
      case '2weeks':
        return addDays(rangeStart, 13);
      case 'month':
        return addDays(rangeStart, 29);
    }
  }, [rangeStart, rangePreset]);

  const startStr = formatDate(rangeStart);
  const endStr = formatDate(rangeEnd);

  const { data, isLoading, error } = useTimeline(startStr, endStr);

  const days = useMemo(() => getDaysInRange(rangeStart, rangeEnd), [rangeStart, rangeEnd]);
  const totalDays = days.length;

  const projectColorMap = useMemo(() => {
    if (!data) return new Map<number, string>();
    const map = new Map<number, string>();
    let idx = 0;
    for (const tech of data.techs) {
      for (const a of tech.assignments) {
        if (!map.has(a.project_id)) {
          map.set(a.project_id, PROJECT_COLORS[idx % PROJECT_COLORS.length]);
          idx++;
        }
      }
    }
    return map;
  }, [data]);

  const handlePrev = () => {
    switch (rangePreset) {
      case '1week':
        setRangeStart((s) => addDays(s, -7));
        break;
      case '2weeks':
        setRangeStart((s) => addDays(s, -14));
        break;
      case 'month':
        setRangeStart((s) => addDays(s, -30));
        break;
    }
  };

  const handleNext = () => {
    switch (rangePreset) {
      case '1week':
        setRangeStart((s) => addDays(s, 7));
        break;
      case '2weeks':
        setRangeStart((s) => addDays(s, 14));
        break;
      case 'month':
        setRangeStart((s) => addDays(s, 30));
        break;
    }
  };

  const handleToday = () => {
    setRangeStart(getMonday(today));
  };

  const handlePresetChange = (preset: RangePreset) => {
    setRangePreset(preset);
    setRangeStart(getMonday(today));
  };

  const [hoveredAssignment, setHoveredAssignment] = useState<{
    assignment: TimelineAssignment;
    techName: string;
    x: number;
    y: number;
  } | null>(null);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-white flex-shrink-0">
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrev}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={handleToday}
            className="px-2 py-1 text-sm rounded hover:bg-gray-100 text-gray-700 font-medium"
          >
            Today
          </button>
          <button
            onClick={handleNext}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <span className="text-sm text-gray-600">
          {formatShortDate(rangeStart)} &ndash; {formatShortDate(rangeEnd)}
        </span>

        <div className="flex items-center gap-1 ml-auto">
          {(['1week', '2weeks', 'month'] as const).map((preset) => (
            <button
              key={preset}
              onClick={() => handlePresetChange(preset)}
              className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                rangePreset === preset
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {preset === '1week' ? '1 Week' : preset === '2weeks' ? '2 Weeks' : 'Month'}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline grid */}
      <div className="flex-1 overflow-auto relative">
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        )}

        {error && (
          <div className="p-8 text-center">
            <p className="text-red-600">Failed to load timeline data.</p>
          </div>
        )}

        {data && !isLoading && (
          <div className="min-w-max">
            {/* Header row */}
            <div
              className="grid sticky top-0 bg-white border-b z-10"
              style={{
                gridTemplateColumns: `200px repeat(${totalDays}, minmax(${rangePreset === 'month' ? '28px' : '48px'}, 1fr))`,
              }}
            >
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-r bg-gray-50">
                Technician
              </div>
              {days.map((day, i) => {
                const isToday = formatDate(day) === formatDate(today);
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                return (
                  <div
                    key={i}
                    className={`px-1 py-2 text-center border-r ${
                      isToday ? 'bg-blue-50' : isWeekend ? 'bg-gray-50' : 'bg-white'
                    }`}
                  >
                    <div className="text-[10px] text-gray-400">{formatDayLabel(day)}</div>
                    <div className={`text-xs ${isToday ? 'font-bold text-blue-600' : 'text-gray-600'}`}>
                      {day.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tech rows */}
            {data.techs.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-sm">
                No assignments found in this date range.
              </div>
            )}

            {data.techs.map((tech) => (
              <div
                key={tech.id}
                className="grid border-b hover:bg-gray-50/50"
                style={{
                  gridTemplateColumns: `200px repeat(${totalDays}, minmax(${rangePreset === 'month' ? '28px' : '48px'}, 1fr))`,
                }}
              >
                {/* Tech name cell */}
                <div className="px-3 py-3 border-r bg-white flex flex-col justify-center">
                  {tech.tech_id != null ? (
                    <a
                      href={`/crew/techs/${tech.tech_id}`}
                      className="text-sm font-medium text-gray-900 truncate hover:text-blue-600 hover:underline"
                    >
                      {tech.first_name} {tech.last_name}
                    </a>
                  ) : (
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {tech.first_name} {tech.last_name}
                    </div>
                  )}
                  {tech.job_title && (
                    <div className="text-xs text-gray-500 truncate">{tech.job_title}</div>
                  )}
                </div>

                {/* Day cells with assignment bars */}
                <div
                  className="col-span-full relative"
                  style={{
                    gridColumn: `2 / ${totalDays + 2}`,
                    minHeight: '48px',
                  }}
                >
                  {/* Background grid lines */}
                  <div className="absolute inset-0 grid" style={{
                    gridTemplateColumns: `repeat(${totalDays}, minmax(${rangePreset === 'month' ? '28px' : '48px'}, 1fr))`,
                  }}>
                    {days.map((day, i) => {
                      const isToday = formatDate(day) === formatDate(today);
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      return (
                        <div
                          key={i}
                          data-date={formatDate(day)}
                          className={`border-r ${
                            isToday ? 'bg-blue-50/50' : isWeekend ? 'bg-gray-50/50' : ''
                          }`}
                        />
                      );
                    })}
                  </div>

                  {/* Assignment bars */}
                  {tech.assignments.map((assignment) => {
                    const aStart = parseLocalDate(assignment.start_date);
                    const aEnd = assignment.end_date ? parseLocalDate(assignment.end_date) : rangeEnd;

                    const barStart = aStart < rangeStart ? rangeStart : aStart;
                    const barEnd = aEnd > rangeEnd ? rangeEnd : aEnd;

                    const startCol = daysBetween(rangeStart, barStart);
                    const endCol = daysBetween(rangeStart, barEnd) + 1;

                    if (startCol >= totalDays || endCol <= 0) return null;

                    const clampedStart = Math.max(0, startCol);
                    const clampedEnd = Math.min(totalDays, endCol);

                    const leftPercent = (clampedStart / totalDays) * 100;
                    const widthPercent = ((clampedEnd - clampedStart) / totalDays) * 100;

                    const color = projectColorMap.get(assignment.project_id) ?? '#6b7280';
                    const startsBeforeRange = aStart < rangeStart;
                    const endsAfterRange = !assignment.end_date || aEnd > rangeEnd;

                    return (
                      <div
                        key={assignment.id}
                        data-assignment-start={assignment.start_date}
                        className="absolute top-2 cursor-pointer transition-opacity hover:opacity-90"
                        style={{
                          left: `${leftPercent}%`,
                          width: `${widthPercent}%`,
                          height: '28px',
                          backgroundColor: color,
                          borderRadius: `${startsBeforeRange ? '0' : '4px'} ${endsAfterRange ? '0' : '4px'} ${endsAfterRange ? '0' : '4px'} ${startsBeforeRange ? '0' : '4px'}`,
                          zIndex: 1,
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoveredAssignment({
                            assignment,
                            techName: `${tech.first_name} ${tech.last_name}`,
                            x: rect.left + rect.width / 2,
                            y: rect.top,
                          });
                        }}
                        onMouseLeave={() => setHoveredAssignment(null)}
                      >
                        <div className="px-2 py-1 text-xs text-white font-medium truncate leading-tight"
                          style={{ lineHeight: '28px' }}
                        >
                          {assignment.project_name}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hover tooltip */}
        {hoveredAssignment && (
          <div
            className="fixed z-50 bg-gray-900 text-white rounded-lg shadow-lg px-3 py-2 text-sm pointer-events-none"
            style={{
              left: hoveredAssignment.x,
              top: hoveredAssignment.y - 8,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div className="font-medium">{hoveredAssignment.assignment.project_name}</div>
            {hoveredAssignment.assignment.project_number && (
              <div className="text-gray-300 text-xs">{hoveredAssignment.assignment.project_number}</div>
            )}
            <div className="text-gray-300 text-xs mt-1">
              {formatDateOnly(hoveredAssignment.assignment.start_date)} &rarr;{' '}
              {formatDateOnly(hoveredAssignment.assignment.end_date, 'Ongoing')}
            </div>
            {hoveredAssignment.assignment.role && (
              <div className="text-gray-300 text-xs">Role: {hoveredAssignment.assignment.role}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
